import type { BadgeFlatDeprecated, BadgeProps, BadgePropsFlat } from '@/shared/types/badge';

const BADGE_CONSUMED_KEYS = new Set<string>([
  'appearance',
  'glyph',
  'text',
  'state',
  'className',
  'label',
  'color',
  'theme',
  'size',
  'stroke',
  'hover',
  'disabled',
  'customColor',
  'icon',
  'iconSlot',
  'iconDuotonePrimary',
  'iconDuotoneSecondary',
  'iconDuotoneOpacityPrimary',
  'iconDuotoneOpacitySecondary',
  'loading',
  'truncateLabel',
  'truncateTooltip',
]);

/**
 * Convierte `BadgeProps` en la forma plana que usa el render interno del componente.
 *
 * Los bloques `appearance`, `glyph`, `text` y `state` tienen prioridad sobre las props planas
 * homónimas (compatibilidad con la API antigua).
 *
 * @param props - Props del consumidor (agrupadas y/o planas)
 * @returns Objeto plano con `label`, colores, icono, truncado y flags de estado ya resueltos
 */
export function mergeGroupedBadgeProps(props: BadgeProps): BadgePropsFlat {
  const { appearance, glyph, text, state, className, ...rest } = props;
  const flat = rest as BadgeFlatDeprecated;

  return {
    className,
    label: text?.label ?? flat.label,
    color: appearance?.color ?? flat.color,
    theme: appearance?.theme ?? flat.theme,
    size: appearance?.size ?? flat.size,
    customColor: appearance?.customColor ?? flat.customColor,
    stroke: appearance?.stroke ?? flat.stroke,
    hover: state?.hover ?? flat.hover,
    disabled: state?.disabled ?? flat.disabled,
    icon: glyph?.icon ?? flat.icon,
    iconSlot: glyph?.iconSlot ?? flat.iconSlot,
    iconDuotonePrimary: glyph?.iconDuotonePrimary ?? flat.iconDuotonePrimary,
    iconDuotoneSecondary: glyph?.iconDuotoneSecondary ?? flat.iconDuotoneSecondary,
    iconDuotoneOpacityPrimary: glyph?.iconDuotoneOpacityPrimary ?? flat.iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary:
      glyph?.iconDuotoneOpacitySecondary ?? flat.iconDuotoneOpacitySecondary,
    loading: state?.loading ?? flat.loading,
    truncateLabel: text?.truncateLabel ?? flat.truncateLabel,
    truncateTooltip: text?.truncateTooltip ?? flat.truncateTooltip,
  };
}

/**
 * Devuelve un objeto de props apto para esparcir en el `<span>` raíz del badge.
 *
 * Elimina claves de la API del componente (`appearance`, `label`, `loading`, etc.) para que no
 * lleguen como atributos DOM inválidos; el resto (p. ej. `data-*`, `aria-*`, `onClick`) se conserva.
 *
 * @param props - Props originales del badge
 * @returns Copia superficial sin las claves consumidas por la implementación
 */
export function omitBadgeConsumedProps(props: BadgeProps): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(props as Record<string, unknown>) };
  for (const k of BADGE_CONSUMED_KEYS) {
    delete out[k];
  }
  return out;
}
